import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { User, Mail, Phone, Lock, ShoppingCart, Loader2, Shield, Clock, CheckCircle } from 'lucide-react';
import { z } from 'zod';
import { trackCompleteRegistration, trackAddToCart, trackInitiateCheckout, trackAddPaymentInfo } from '@/lib/metaPixel';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

declare global {
  interface Window {
    Cashfree: any;
  }
}

const signupSchema = z.object({
  fullName: z.string().trim().min(1, { message: 'Full name is required' }).max(100),
  email: z.string().trim().email({ message: 'Invalid email format' }).max(255),
  mobile: z.string().regex(/^[6-9]\d{9}$/, { message: 'Enter valid 10-digit mobile number' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
});

const SignupCheckout: React.FC = () => {
  const navigate = useNavigate();
  const { user, signUp } = useSupabaseAuth();
  const { toast } = useToast();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // If user is already logged in, redirect to payment directly
  useEffect(() => {
    if (user) {
      startPaymentForLoggedInUser();
    }
  }, [user]);

  const startPaymentForLoggedInUser = async () => {
    if (!window.Cashfree) {
      toast({ title: "Loading payment...", description: "Please wait" });
      setTimeout(() => startPaymentForLoggedInUser(), 500);
      return;
    }

    setIsLoading(true);
    try {
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user!.id)
        .single();

      const orderId = `order_${Date.now()}`;
      
      try { trackAddToCart(); trackInitiateCheckout(); } catch (e) {}

      const { data, error } = await supabase.functions.invoke('create-cashfree-order', {
        body: {
          orderId,
          orderAmount: 149,
          customerName: userProfile?.full_name || 'Customer',
          customerEmail: userProfile?.email || user!.email || 'customer@example.com',
          customerPhone: userProfile?.mobile || '9999999999',
        },
      });

      if (error || !data?.success) {
        throw new Error(data?.error || error?.message || 'Failed to create order');
      }

      await supabase.from('payments').insert({
        user_id: user!.id,
        order_id: orderId,
        amount: 149,
        status: 'pending',
      });

      try { trackAddPaymentInfo(); } catch (e) {}

      const cashfree = window.Cashfree({ mode: 'production' });
      cashfree.checkout({
        paymentSessionId: data.paymentSessionId,
        redirectTarget: '_self',
      });

    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Payment failed';
      toast({ title: "Error", description: msg, variant: "destructive" });
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate input
    const result = signupSchema.safeParse({ fullName, email, mobile, password });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);

    try {
      // Sign up the user
      const { error: signUpError, data: authData } = await signUp(email, password, fullName, mobile);

      if (signUpError) {
        let errorMessage = "An error occurred during registration.";
        if (signUpError.message.includes('already registered')) {
          errorMessage = "An account with this email already exists. Please login.";
        } else if (signUpError.message.includes('duplicate')) {
          errorMessage = "This email or mobile is already registered.";
        } else if (signUpError.message) {
          errorMessage = signUpError.message;
        }
        toast({ title: "Registration Failed", description: errorMessage, variant: "destructive" });
        setIsLoading(false);
        return;
      }

      // Store credentials for dashboard display
      localStorage.setItem('generatedCredentials', JSON.stringify({ email, password }));

      // Save password for admin viewing
      if (authData?.user?.id) {
        supabase.from('profiles')
          .update({ auto_password: password })
          .eq('id', authData.user.id)
          .then(() => {});
      }

      try { trackCompleteRegistration({ content_name: 'Signup Checkout', status: 'success' }); } catch (e) {}
      try { trackAddToCart(); } catch (e) {}

      // User is now logged in automatically, start payment
      toast({ title: "Account created!", description: "Redirecting to payment..." });

      // Wait a moment for auth state to update
      setTimeout(async () => {
        await startPaymentAfterSignup(authData?.user?.id, fullName, email, mobile);
      }, 500);

    } catch (err) {
      toast({ title: "Error", description: "Something went wrong. Please try again.", variant: "destructive" });
      setIsLoading(false);
    }
  };

  const startPaymentAfterSignup = async (userId: string | undefined, name: string, userEmail: string, userMobile: string) => {
    if (!window.Cashfree) {
      toast({ title: "Loading payment...", description: "Please wait" });
      setTimeout(() => startPaymentAfterSignup(userId, name, userEmail, userMobile), 500);
      return;
    }

    try {
      const orderId = `order_${Date.now()}`;
      
      try { trackInitiateCheckout(); } catch (e) {}

      const { data, error } = await supabase.functions.invoke('create-cashfree-order', {
        body: {
          orderId,
          orderAmount: 149,
          customerName: name,
          customerEmail: userEmail,
          customerPhone: userMobile || '9999999999',
        },
      });

      if (error || !data?.success) {
        throw new Error(data?.error || error?.message || 'Failed to create order');
      }

      // Store payment record
      if (userId) {
        await supabase.from('payments').insert({
          user_id: userId,
          order_id: orderId,
          amount: 149,
          status: 'pending',
        });
      }

      try { trackAddPaymentInfo(); } catch (e) {}

      // Redirect to Cashfree
      const cashfree = window.Cashfree({ mode: 'production' });
      cashfree.checkout({
        paymentSessionId: data.paymentSessionId,
        redirectTarget: '_self',
      });

    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Payment failed';
      toast({ title: "Error", description: msg, variant: "destructive" });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Signup & Checkout - TOPPERS BUDDY</title>
        <meta name="description" content="Create your account and get instant access to Class 10 Science Notes" />
      </Helmet>

      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        
        <main className="flex-1 py-8 px-4">
          <div className="container max-w-lg mx-auto">
            <Card className="shadow-xl border-primary/20">
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl font-bold">
                  Create Your Account
                </CardTitle>
                <CardDescription className="text-base">
                  Sign up to purchase Class 10 Science Notes
                </CardDescription>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      Full Name
                    </Label>
                    <Input
                      id="fullName"
                      placeholder="Enter your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className={errors.fullName ? 'border-destructive' : ''}
                      disabled={isLoading}
                    />
                    {errors.fullName && <p className="text-xs text-destructive">{errors.fullName}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mobile" className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      WhatsApp Mobile Number
                    </Label>
                    <Input
                      id="mobile"
                      placeholder="Enter 10-digit mobile number"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className={errors.mobile ? 'border-destructive' : ''}
                      disabled={isLoading}
                    />
                    {errors.mobile && <p className="text-xs text-destructive">{errors.mobile}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      Email ID
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={errors.email ? 'border-destructive' : ''}
                      disabled={isLoading}
                    />
                    {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-muted-foreground" />
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Create a password (min 6 characters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={errors.password ? 'border-destructive' : ''}
                      disabled={isLoading}
                    />
                    {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                  </div>

                  {/* Price Summary */}
                  <div className="bg-muted/50 rounded-lg p-4 mt-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-muted-foreground">Class 10 Science Notes</span>
                      <span className="line-through text-muted-foreground">₹499</span>
                    </div>
                    <div className="flex justify-between items-center text-lg font-bold">
                      <span>Total</span>
                      <span className="text-primary">₹149</span>
                    </div>
                    <div className="text-center mt-2">
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        You save ₹350 (70% OFF)
                      </span>
                    </div>
                  </div>

                  {/* Buy Now Button */}
                  <Button 
                    type="submit"
                    variant="hero" 
                    className="w-full h-14 text-lg gap-2 mt-4" 
                    size="lg"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-5 w-5" />
                        BUY NOW - ₹149
                      </>
                    )}
                  </Button>

                  {/* Trust Badges */}
                  <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground mt-4">
                    <div className="flex items-center gap-1">
                      <Shield className="h-3 w-3 text-primary" />
                      <span>Secure Payment</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3 text-primary" />
                      <span>Instant Access</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-primary" />
                      <span>Lifetime Access</span>
                    </div>
                  </div>
                </form>

                {/* Already have account link */}
                <div className="text-center mt-6 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{' '}
                    <button 
                      onClick={() => navigate('/login')}
                      className="text-primary hover:underline font-medium"
                    >
                      Login here
                    </button>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default SignupCheckout;
